import React, { useCallback, useMemo, useRef, useState } from "react";
import { toast } from '../../../../components/common/Toast/toast';
import debounce from "lodash/debounce";
import { createTrainer, updateTrainer } from "../../../../../services/TrainerService.js";
import { getUsers } from "../../../../../services/UserSerivce.js";
import NewCustomAsyncDropdown from "../../../../components/common/NewCustomAsyncSelectDropdown.jsx";
import Modal from "../../components/Modal.jsx";

const TrainerFormModal = ({ trainer, onClose, onSaved }) => {
  const isEditing = Boolean(trainer);
  const [specialization, setSpecialization] = useState(trainer?.specialization || "");
  const [experience, setExperience] = useState(trainer?.experience || "");
  const [userOptions, setUserOptions] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState(
    isEditing
      ? { value: trainer.id, label: `${trainer.trainerName || ""} (${trainer.trainerEmail || ""})`, user: { id: trainer.id, firstName: trainer.trainerName || "", lastName: "", email: trainer.trainerEmail || "" } }
      : null
  );
  const [saving, setSaving] = useState(false);
  const hasLoadedInitial = useRef(false);

  const loadUsers = useCallback(async (search = "") => {
    setLoadingUsers(true);
    try {
      const response = await getUsers(20, 1, search, "firstName");
      const users = response.data || [];
      setUserOptions(users.map((u) => ({
        value: u.id,
        label: `${u.firstName} ${u.lastName} - (${u.email})`,
        user: u,
      })));
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const handleSearch = useMemo(() => debounce((val) => loadUsers(val), 500), [loadUsers]);

  const handleDropdownOpen = () => {
    if (!hasLoadedInitial.current && !userOptions.length) {
      loadUsers("");
      hasLoadedInitial.current = true;
    }
  };

  const handleSelect = (options) => {
    const selected = options && options.length ? options[0] : null;
    setSelectedUser(selected);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!isEditing && !selectedUser) {
      toast.error("Please select a user");
      return;
    }
    if (!specialization.trim() || !experience.trim()) {
      toast.error("Specialization and experience are required");
      return;
    }
    setSaving(true);
    try {
      const payload = isEditing
        ? { specialization: specialization.trim(), experience: experience.trim() }
        : {
            trainerId: selectedUser.value,
            trainerName: `${selectedUser.user.firstName} ${selectedUser.user.lastName}`.trim(),
            trainerEmail: selectedUser.user.email,
            specialization: specialization.trim(),
            experience: experience.trim(),
          };
      if (isEditing) {
        await updateTrainer(trainer.id, payload);
        toast.success("Trainer updated");
      } else {
        await createTrainer(payload);
        toast.success("Trainer added");
      }
      onSaved();
    } catch (error) {
      if (error?.response?.status === 409) {
        toast.error("A trainer with this email already exists");
      } else {
        toast.error(error?.response?.data?.message || "Failed to save trainer");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      over
      eyebrow="Trainer"
      title={isEditing ? "Edit trainer" : "New trainer"}
      sub="Which user teaches, what they teach, and for how long they have."
      onClose={onClose}
      size="form"
      labelledBy="cls-trainer-form-title"
      footer={
        <>
          <div className="pgm-actions">
            <button type="button" className="pgm-btn" onClick={onClose}>
              Cancel
            </button>
          </div>
          <button
            type="submit"
            form="cls-trainer-form"
            className="pg-btn pg-btn--main"
            disabled={saving}
          >
            {saving ? "Saving..." : isEditing ? "Save changes" : "Create trainer"}
          </button>
        </>
      }
    >
      <form id="cls-trainer-form" className="att-form" noValidate onSubmit={submit}>
        <div className="pg-field">
          <label className="pg-label" htmlFor="cls-tf-user">
            User
          </label>
          {isEditing ? (
            <input
              id="cls-tf-user"
              className="pg-input"
              value={selectedUser?.label || ""}
              disabled
            />
          ) : (
            <div id="cls-tf-user">
              <NewCustomAsyncDropdown
                options={userOptions}
                value={selectedUser ? [selectedUser] : []}
                onChange={handleSelect}
                placeholder="Type to search users..."
                isMulti={false}
                isSearchable
                isLoading={loadingUsers}
                onInputChange={handleSearch}
                onMenuOpen={handleDropdownOpen}
                loadingMessage="Loading users..."
                noOptionsMessage="No users available"
              />
            </div>
          )}
        </div>

        <div className="mcq-options">
          <div className="pg-field">
            <label className="pg-label" htmlFor="cls-tf-spec">
              Course / specialization
            </label>
            <input
              id="cls-tf-spec"
              className="pg-input"
              value={specialization}
              onChange={(e) => setSpecialization(e.target.value)}
              placeholder="e.g. Java, Python, React"
            />
          </div>

          <div className="pg-field">
            <label className="pg-label" htmlFor="cls-tf-exp">
              Experience (years)
            </label>
            <input
              id="cls-tf-exp"
              className="pg-input"
              value={experience}
              onChange={(e) => setExperience(e.target.value)}
              placeholder="e.g. 4"
            />
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default TrainerFormModal;
